// LooperEngine — pure JS class that owns the multitrack player and
// the overdub recorder. No React, no axios, no DOM. Holds the
// AudioContext, the buffer cache, the active sources Map, the loop
// watchdog and the RAF, and notifies subscribers via the
// useSyncExternalStore protocol.
//
// Design notes:
// - The class never imports React. Consumers wire it up via
//   useSyncExternalStore(engine.subscribe, engine.getSnapshot).
// - getSnapshot returns the same object reference between calls until
//   internal state changes; React's equality check relies on this.
// - The activeSources Map and the loop watchdog interval are deliberate
//   - they exist because `onended` was unreliable across browsers
//   (see the bug history in the previous in-line comments). Keep them.
// - The class accepts an injected `fetchAudioData(id) -> Promise<string>`
//   so it doesn't take a dependency on axios or the backend URL.

const INITIAL_SNAPSHOT = Object.freeze({
  isPlaying: false,
  isLoading: false,
  selectedTrackIds: [],
  trackVolumes: {},
  trackMuted: {},
  trackSoloed: {},
  masterVolume: 80,
  loop: false,
  elapsed: 0,
  duration: 0,
  overdubState: 'idle', // idle | armed | recording | processing
  overdubMultiplier: 1,
  overdubHistory: [],
  bufferIds: [],
  version: 0,
});

const NOTIFY_DEBOUNCE_MS = 100; // 10 Hz cap on snapshot notifications

export default class LooperEngine {
  constructor({ fetchAudioData, audioContextFactory } = {}) {
    this._fetchAudioData = fetchAudioData || (() => {
      throw new Error('LooperEngine: fetchAudioData not provided');
    });
    this._audioContextFactory = audioContextFactory ||
      (() => new (window.AudioContext || window.webkitAudioContext)());

    // React subscriber protocol
    this._listeners = new Set();
    this._snapshot = { ...INITIAL_SNAPSHOT };
    this._notifyTimer = null;

    // Audio engine refs (mirrors the original RecorderPage refs)
    this._ctx = null;
    this._masterGain = null;
    this._bufferCache = new Map();        // id -> AudioBuffer
    this._activeSources = new Map();      // id -> { source, gain }
    this._loopWatchdog = null;
    this._raf = null;
    this._startedAt = 0;
    this._lastLoopIndex = 0;
    this._buffersForLoop = [];

    // Overdub
    this._overdubStream = null;
    this._overdubRecorder = null;
    this._overdubChunks = [];
    this._overdubLoopCount = 0;
    this._overdubTargetLoops = 1;
    this._onOverdubFinished = null;
  }

  // ============ React subscriber protocol ============

  subscribe = (listener) => {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  };

  getSnapshot = () => this._snapshot;

  _update(patch) {
    this._snapshot = {
      ...this._snapshot,
      ...patch,
      version: this._snapshot.version + 1,
    };
    if (this._notifyTimer) return;
    this._notifyTimer = setTimeout(() => {
      this._notifyTimer = null;
      this._listeners.forEach((l) => {
        try { l(); } catch (_) {}
      });
    }, NOTIFY_DEBOUNCE_MS);
  }

  _updateNow(patch) {
    // For state changes that must reach React synchronously (e.g.
    // play/stop button visuals), skip the debounce.
    this._snapshot = {
      ...this._snapshot,
      ...patch,
      version: this._snapshot.version + 1,
    };
    this._listeners.forEach((l) => {
      try { l(); } catch (_) {}
    });
  }

  // ============ AudioContext lifecycle ============

  _ensureCtx() {
    if (!this._ctx) {
      this._ctx = this._audioContextFactory();
    }
    return this._ctx;
  }

  async _ensureCtxResumed() {
    const ctx = this._ensureCtx();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    return ctx;
  }

  _ensureMasterGain() {
    const ctx = this._ensureCtx();
    if (!this._masterGain || this._masterGain.context !== ctx) {
      this._masterGain = ctx.createGain();
      this._masterGain.connect(ctx.destination);
    }
    this._masterGain.gain.value = this._snapshot.masterVolume / 100;
    return this._masterGain;
  }

  // ============ Track selection ============

  async selectTrack(id) {
    const sel = new Set(this._snapshot.selectedTrackIds);
    if (sel.has(id)) return;
    sel.add(id);
    this._updateNow({ selectedTrackIds: [...sel] });

    // Pre-fetch + decode so playback is instant
    if (!this._bufferCache.has(id) && !id.startsWith('local_')) {
      try {
        await this._loadTrackBuffer(id);
      } catch (err) {
        // Surface but don't block selection
        console.warn('LooperEngine.selectTrack decode failed', id, err);
      }
    }
  }

  unselectTrack(id) {
    // Stop the source for that track if active
    const entry = this._activeSources.get(id);
    if (entry) {
      try { entry.source.stop(); } catch (_) {}
      try { entry.source.disconnect(); } catch (_) {}
      try { entry.gain.disconnect(); } catch (_) {}
      this._activeSources.delete(id);
    }
    const sel = new Set(this._snapshot.selectedTrackIds);
    sel.delete(id);
    this._updateNow({ selectedTrackIds: [...sel] });
  }

  async _loadTrackBuffer(id) {
    const ctx = await this._ensureCtxResumed();
    const dataUrl = await this._fetchAudioData(id);
    if (!dataUrl || dataUrl.length < 100) {
      throw new Error('Audio non disponibile');
    }
    const base64 = dataUrl.split(',')[1];
    const ab = LooperEngine._base64ToArrayBuffer(base64);
    const audioBuffer = await ctx.decodeAudioData(ab);
    this._bufferCache.set(id, audioBuffer);
    this._update({ bufferIds: [...this._bufferCache.keys()] });
    return audioBuffer;
  }

  cacheLocalBuffer(localId, audioBuffer) {
    // Used by session restore where the buffer is already decoded.
    this._bufferCache.set(localId, audioBuffer);
    this._update({ bufferIds: [...this._bufferCache.keys()] });
  }

  // ============ Mixer ============

  setVolume(id, value) {
    const next = { ...this._snapshot.trackVolumes, [id]: value };
    this._updateNow({ trackVolumes: next });
    const entry = this._activeSources.get(id);
    if (entry?.gain) {
      try { entry.gain.gain.value = value / 100; } catch (_) {}
    }
  }

  toggleMute(id) {
    const next = { ...this._snapshot.trackMuted, [id]: !this._snapshot.trackMuted[id] };
    this._updateNow({ trackMuted: next });
    this._applyMuteSoloGains(next, this._snapshot.trackSoloed);
  }

  toggleSolo(id) {
    const next = { ...this._snapshot.trackSoloed, [id]: !this._snapshot.trackSoloed[id] };
    this._updateNow({ trackSoloed: next });
    this._applyMuteSoloGains(this._snapshot.trackMuted, next);
  }

  setMaster(value) {
    this._updateNow({ masterVolume: value });
    if (this._masterGain) {
      try { this._masterGain.gain.value = value / 100; } catch (_) {}
    }
  }

  setLoop(bool) {
    this._updateNow({ loop: !!bool });
  }

  _applyMuteSoloGains(muted, soloed) {
    const anySolo = Object.values(soloed).some((v) => v);
    for (const id of this._snapshot.selectedTrackIds) {
      const entry = this._activeSources.get(id);
      if (!entry?.gain) continue;
      let vol = (this._snapshot.trackVolumes[id] ?? 80) / 100;
      if (muted[id]) vol = 0;
      else if (anySolo && !soloed[id]) vol = 0;
      try { entry.gain.gain.value = vol; } catch (_) {}
    }
  }

  // ============ Transport ============

  async play() {
    if (this._snapshot.selectedTrackIds.length === 0) return;
    this._updateNow({ isLoading: true });
    try {
      await this._ensureCtxResumed();
      this.stop(false); // hard reset of any previous run
      this._ensureMasterGain();

      // Make sure every selected track has a decoded buffer.
      const buffers = [];
      for (const id of this._snapshot.selectedTrackIds) {
        if (this._bufferCache.has(id)) {
          buffers.push({ id, buffer: this._bufferCache.get(id) });
          continue;
        }
        if (id.startsWith('local_')) continue;
        try {
          const buf = await this._loadTrackBuffer(id);
          buffers.push({ id, buffer: buf });
        } catch (err) {
          console.warn('LooperEngine.play decode failed', id, err);
        }
      }

      if (buffers.length === 0) {
        this._updateNow({ isLoading: false });
        return;
      }

      const maxDur = Math.max(...buffers.map((b) => b.buffer.duration));
      this._buffersForLoop = buffers;
      this._lastLoopIndex = 0;

      const startAt = this._ctx.currentTime + 0.05;
      this._startedAt = startAt;
      this._startSources(startAt);

      this._updateNow({
        isPlaying: true,
        isLoading: false,
        duration: maxDur,
        elapsed: 0,
      });

      this._startRaf();
      this._startLoopWatchdog();
    } catch (err) {
      console.error('LooperEngine.play error', err);
      this._updateNow({ isLoading: false });
      throw err;
    }
  }

  _startSources(startAt) {
    this._stopAllSources();
    const ctx = this._ctx;
    const anySolo = Object.values(this._snapshot.trackSoloed).some((v) => v);

    for (const { id, buffer } of this._buffersForLoop) {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      let vol = (this._snapshot.trackVolumes[id] ?? 80) / 100;
      if (this._snapshot.trackMuted[id]) vol = 0;
      else if (anySolo && !this._snapshot.trackSoloed[id]) vol = 0;
      gain.gain.value = vol;
      source.connect(gain);
      gain.connect(this._masterGain);

      this._activeSources.set(id, { source, gain });
      source.onended = () => {
        // Delete from Map - the loop-watchdog handles restart if loop
        // is on, otherwise the snapshot stays isPlaying:true until
        // stop() is called. The Map size becoming 0 is the signal the
        // watchdog uses.
        this._activeSources.delete(id);
      };
      try { source.start(startAt); } catch (_) {}
    }
  }

  _stopAllSources() {
    for (const [, entry] of this._activeSources.entries()) {
      try { entry.source.stop(); } catch (_) {}
      try { entry.source.disconnect(); } catch (_) {}
      try { entry.gain.disconnect(); } catch (_) {}
    }
    this._activeSources.clear();
  }

  _startRaf() {
    const tick = () => {
      if (!this._ctx || !this._snapshot.isPlaying) {
        this._raf = null;
        return;
      }
      const now = this._ctx.currentTime;
      const elapsed = now - this._startedAt;
      const loopDur = this._snapshot.duration;
      if (loopDur > 0 && elapsed >= 0) {
        const normalized = elapsed % loopDur;
        this._update({ elapsed: Math.max(0, Math.min(normalized, loopDur)) });

        const currentLoopIndex = Math.floor(elapsed / loopDur);
        if (currentLoopIndex > this._lastLoopIndex) {
          this._lastLoopIndex = currentLoopIndex;
          // Hooks: overdub state machine listens to loop boundaries
          if (this._snapshot.overdubState === 'armed') {
            this._overdubLoopCount = currentLoopIndex;
            this._startOverdubInternal();
          }
        }
        if (this._snapshot.overdubState === 'recording') {
          const loopsSinceStart = currentLoopIndex - this._overdubLoopCount;
          if (loopsSinceStart >= this._overdubTargetLoops) {
            this._stopOverdubInternal();
          }
        }
      }
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  _startLoopWatchdog() {
    if (this._loopWatchdog) clearInterval(this._loopWatchdog);
    this._loopWatchdog = setInterval(() => {
      if (!this._snapshot.isPlaying) {
        clearInterval(this._loopWatchdog);
        this._loopWatchdog = null;
        return;
      }
      if (this._activeSources.size === 0) {
        if (this._snapshot.loop && this._buffersForLoop.length > 0) {
          const restartAt = this._ctx.currentTime + 0.01;
          this._startedAt = restartAt;
          this._startSources(restartAt);
        } else {
          this.stop(true);
        }
      }
    }, 100);
  }

  stop(updateState = true) {
    this._stopAllSources();
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = null;
    }
    if (this._loopWatchdog) {
      clearInterval(this._loopWatchdog);
      this._loopWatchdog = null;
    }
    this._lastLoopIndex = 0;

    // Cancel any overdub
    if (
      this._snapshot.overdubState === 'armed' ||
      this._snapshot.overdubState === 'recording'
    ) {
      if (this._overdubRecorder && this._snapshot.overdubState === 'recording') {
        try { this._overdubRecorder.stop(); } catch (_) {}
      }
      this._overdubChunks = [];
      if (updateState) {
        this._updateNow({ overdubState: 'idle' });
      }
    }

    if (updateState) {
      this._updateNow({ isPlaying: false, elapsed: 0 });
    }
  }

  panic() {
    // Force-stop every audio object and reset all UI flags.
    try { this._stopAllSources(); } catch (_) {}
    if (this._raf) {
      try { cancelAnimationFrame(this._raf); } catch (_) {}
      this._raf = null;
    }
    if (this._loopWatchdog) {
      try { clearInterval(this._loopWatchdog); } catch (_) {}
      this._loopWatchdog = null;
    }
    if (this._overdubRecorder) {
      try { this._overdubRecorder.stop(); } catch (_) {}
    }
    this._overdubChunks = [];
    this._updateNow({
      isPlaying: false,
      isLoading: false,
      elapsed: 0,
      overdubState: 'idle',
    });
  }

  // ============ Overdub ============

  armOverdub({ stream, multiplier = 1, onFinished }) {
    if (!stream || !stream.active) {
      throw new Error('LooperEngine.armOverdub: stream is missing or inactive');
    }
    if (!this._snapshot.isPlaying || !this._snapshot.loop) {
      throw new Error('LooperEngine.armOverdub: requires loop playback');
    }
    this._overdubStream = stream;
    this._overdubTargetLoops = multiplier;
    this._onOverdubFinished = onFinished || null;
    this._updateNow({
      overdubState: 'armed',
      overdubMultiplier: multiplier,
    });
  }

  _startOverdubInternal() {
    if (!this._overdubStream || !this._overdubStream.active) {
      this._updateNow({ overdubState: 'idle' });
      return;
    }
    try {
      this._overdubChunks = [];
      this._overdubRecorder = new MediaRecorder(this._overdubStream);
      this._overdubRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this._overdubChunks.push(e.data);
      };
      this._overdubRecorder.onstop = async () => {
        const blob = new Blob(this._overdubChunks, {
          type: this._overdubRecorder.mimeType || 'audio/webm',
        });
        this._updateNow({ overdubState: 'processing' });
        if (this._onOverdubFinished) {
          try { await this._onOverdubFinished(blob); } catch (err) {
            console.error('LooperEngine onOverdubFinished error', err);
          }
        }
        this._updateNow({ overdubState: 'idle' });
        this._overdubChunks = [];
        this._overdubRecorder = null;
        this._onOverdubFinished = null;
      };
      this._overdubRecorder.start();
      this._updateNow({ overdubState: 'recording' });
    } catch (err) {
      console.error('LooperEngine._startOverdubInternal error', err);
      this._updateNow({ overdubState: 'idle' });
    }
  }

  _stopOverdubInternal() {
    if (this._overdubRecorder && this._snapshot.overdubState === 'recording') {
      try { this._overdubRecorder.stop(); } catch (_) {}
    }
  }

  cancelOverdub() {
    if (this._overdubRecorder) {
      try { this._overdubRecorder.stop(); } catch (_) {}
    }
    this._overdubChunks = [];
    this._overdubRecorder = null;
    this._onOverdubFinished = null;
    this._updateNow({ overdubState: 'idle' });
  }

  pushOverdubHistory(trackId) {
    const next = [...this._snapshot.overdubHistory, trackId];
    this._updateNow({ overdubHistory: next });
  }

  popOverdubHistory() {
    const history = this._snapshot.overdubHistory;
    if (history.length === 0) return null;
    const trackId = history[history.length - 1];
    this._updateNow({ overdubHistory: history.slice(0, -1) });
    return trackId;
  }

  // ============ Lifecycle ============

  destroy() {
    this.stop(false);
    if (this._notifyTimer) {
      clearTimeout(this._notifyTimer);
      this._notifyTimer = null;
    }
    if (this._masterGain) {
      try { this._masterGain.disconnect(); } catch (_) {}
      this._masterGain = null;
    }
    if (this._ctx) {
      try { this._ctx.close(); } catch (_) {}
      this._ctx = null;
    }
    this._bufferCache.clear();
    this._listeners.clear();
  }

  // ============ Static helpers ============

  static _base64ToArrayBuffer(base64) {
    const binary = atob(base64);
    const len = binary.length;
    const ab = new ArrayBuffer(len);
    const view = new Uint8Array(ab);
    for (let i = 0; i < len; i++) view[i] = binary.charCodeAt(i);
    return ab;
  }
}

export { INITIAL_SNAPSHOT };
